import { useLanguage, t } from "../../../i18n";
import React, { useMemo, useState, useEffect } from "react";
import { Button, Modal, Spinner } from "react-bootstrap";
import { HiOutlineChevronLeft, HiOutlineChevronRight, HiOutlineX } from "react-icons/hi";
import { HiHeart, HiOutlineHeart } from "react-icons/hi2";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import ROUTES from "../../../config/routes";
import toggleRoomShortlist from "../../../features/shortlist/services/toggleRoomShortlist";
import { useAuth } from "../../../features/auth/context/AuthContext";
import "./ListingImageGallery.css";
export default function ListingImageGallery({
  images = [],
  title = "Room Images",
  listingId,
  isShortlisted = false,
  isOwner = false,
  onShortlistChange
}) {
  useLanguage();
  const navigate = useNavigate();
  const {
    isAuthenticated
  } = useAuth();
  const normalizedImages = useMemo(() => {
    return images.map((item, index) => {
      if (typeof item === "string") {
        return {
          id: index + 1,
          name: `Room Image ${index + 1}`,
          url: item
        };
      }
      return {
        id: item?.id || index + 1,
        name: item?.name || `Room Image ${index + 1}`,
        url: item?.url || item?.image || item?.src || ""
      };
    });
  }, [images]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [showViewer, setShowViewer] = useState(false);
  const [isShortlistSubmitting, setIsShortlistSubmitting] = useState(false);
  const [localIsShortlisted, setLocalIsShortlisted] = useState(Boolean(isShortlisted));
  useEffect(() => {
    setLocalIsShortlisted(Boolean(isShortlisted));
  }, [isShortlisted]);
  const totalImages = normalizedImages.length;
  const activeImage = normalizedImages[activeIndex];
  const goToPrevious = () => {
    setActiveIndex(prev => prev === 0 ? totalImages - 1 : prev - 1);
  };
  const goToNext = () => {
    setActiveIndex(prev => prev === totalImages - 1 ? 0 : prev + 1);
  };
  const handleToggleShortlist = async event => {
    event.preventDefault();
    event.stopPropagation();
    if (!isAuthenticated) {
      navigate(ROUTES.LOGIN);
      return;
    }
    if (isOwner) {
      return;
    }
    if (!listingId || isShortlistSubmitting) return;
    try {
      setIsShortlistSubmitting(true);
      const response = await toggleRoomShortlist(listingId);
      const nextIsShortlisted = Boolean(response?.is_favourite);
      setLocalIsShortlisted(nextIsShortlisted);
      if (nextIsShortlisted) {
        toast.success(t("Room has been shortlisted"));
      } else {
        toast.success(t("Removed from Shortlist"));
      }
      if (onShortlistChange) {
        onShortlistChange(nextIsShortlisted);
      }
    } catch (error) {
      toast.error(t(error?.response?.data?.message || error?.message || "Failed to update shortlist."));
    } finally {
      setIsShortlistSubmitting(false);
    }
  };
  if (!totalImages) {
    return null;
  }
  return <>
      <div className="bb-listing-gallery">
        <div className="bb-listing-gallery__main" onClick={() => setShowViewer(true)} role="button" tabIndex={0}>

          <img src={activeImage?.url} alt={t(activeImage?.name || title)} className="bb-listing-gallery__main-image" />


          {isOwner ? <div className="bb-listing-gallery__owner-badge">{t("Listed by You")}</div> : <button type="button" className={`bb-listing-gallery__shortlist-btn ${localIsShortlisted ? "active" : ""}`} onClick={handleToggleShortlist} aria-label={t(localIsShortlisted ? "Remove from shortlist" : "Add to shortlist")} disabled={isShortlistSubmitting}>

              {isShortlistSubmitting ? <Spinner animation="border" size="sm" /> : localIsShortlisted ? <HiHeart /> : <HiOutlineHeart />}
            </button>}

          {totalImages > 1 ? <>
              <button type="button" className="bb-listing-gallery__nav bb-listing-gallery__nav--left" onClick={e => {
            e.stopPropagation();
            goToPrevious();
          }}>

                <HiOutlineChevronLeft className="bb-directional-icon" />
              </button>

              <button type="button" className="bb-listing-gallery__nav bb-listing-gallery__nav--right" onClick={e => {
            e.stopPropagation();
            goToNext();
          }}>

                <HiOutlineChevronRight className="bb-directional-icon" />
              </button>
            </> : null}

          <div className="bb-listing-gallery__count">
            {t(activeIndex + 1)} / {t(totalImages)}
          </div>
        </div>

        {totalImages > 1 ? <div className="bb-listing-gallery__thumbs">
            {normalizedImages.map((image, index) => <button key={image.id} type="button" className={`bb-listing-gallery__thumb ${index === activeIndex ? "active" : ""}`} onClick={() => setActiveIndex(index)}>

                <img src={image.url} alt={t(image.name)} />
              </button>)}
          </div> : null}
      </div>

      <Modal show={showViewer} onHide={() => setShowViewer(false)} centered size="xl" dialogClassName="bb-listing-gallery-modal">

        <Modal.Body className="bb-listing-gallery-modal__body">
          <Button variant="light" className="bb-listing-gallery-modal__close" onClick={() => setShowViewer(false)}>

            <HiOutlineX />
          </Button>

          <div className="bb-listing-gallery-modal__stage">
            <img src={activeImage?.url} alt={t(activeImage?.name || title)} className="bb-listing-gallery-modal__image" />


            {totalImages > 1 ? <>
                <button type="button" className="bb-listing-gallery-modal__nav bb-listing-gallery-modal__nav--left" onClick={goToPrevious}>

                  <HiOutlineChevronLeft className="bb-directional-icon" />
                </button>

                <button type="button" className="bb-listing-gallery-modal__nav bb-listing-gallery-modal__nav--right" onClick={goToNext}>

                  <HiOutlineChevronRight className="bb-directional-icon" />
                </button>
              </> : null}

            <div className="bb-listing-gallery-modal__count">
              {t(activeIndex + 1)} / {t(totalImages)}
            </div>
          </div>

          {totalImages > 1 ? <div className="bb-listing-gallery-modal__thumbs">
              {normalizedImages.map((image, index) => <button key={image.id} type="button" className={`bb-listing-gallery-modal__thumb ${index === activeIndex ? "active" : ""}`} onClick={() => setActiveIndex(index)}>

                  <img src={image.url} alt={t(image.name)} />
                </button>)}
            </div> : null}
        </Modal.Body>
      </Modal>
    </>;
}
